export const AUTHORIZED_PAYMENT_STATUSES = ['requires_capture', 'succeeded'];
export const PROCESSING_PAYMENT_STATUS = 'processing';
const PAYMENT_DRAFT_CLEAR_STATUSES = ['canceled'];
export const PAYMENT_PROCESSING_MESSAGE =
  'Deine Zahlung wird noch verarbeitet. Bitte warte kurz und versuche es erneut.';
export const BOOKING_CHECKOUT_IN_PROGRESS_MESSAGE =
  'Eine Buchung für dieses Studio wird bereits in einem anderen Tab vorbereitet. Bitte schließe den Vorgang dort ab oder versuche es in Kürze erneut.';

const PAYMENT_DRAFT_STORAGE_PREFIX = 'stuBookingPaymentDraft';
const CHECKOUT_LOCK_STORAGE_PREFIX = 'stuBookingCheckoutLock';
const CHECKOUT_LOCK_OWNER_KEY = 'stuBookingCheckoutLockOwner';
const CHECKOUT_LOCK_TTL_MS = 2 * 60 * 1000;
const POLL_DELAYS_MS = [1000, 2000, 3000];

export const normalizePaymentIntentStatus = (status) => (
  typeof status === 'string' ? status.toLowerCase() : ''
);

export const isAuthorizedPaymentStatus = (status) => (
  AUTHORIZED_PAYMENT_STATUSES.includes(normalizePaymentIntentStatus(status))
);

export const isProcessingPaymentStatus = (status) => (
  normalizePaymentIntentStatus(status) === PROCESSING_PAYMENT_STATUS
);

export const createPaymentProcessingError = () => {
  const error = new Error(PAYMENT_PROCESSING_MESSAGE);
  error.name = 'PaymentProcessingPendingError';
  error.userFacing = true;
  return error;
};

const createUnexpectedPaymentStatusError = (status) => {
  const normalizedStatus = normalizePaymentIntentStatus(status) || 'unknown';
  const error = new Error(`Unexpected PaymentIntent status: ${normalizedStatus}`);
  error.paymentIntentStatus = normalizedStatus;
  return error;
};

export const shouldClearPaymentDraftForStatus = (status) => (
  PAYMENT_DRAFT_CLEAR_STATUSES.includes(normalizePaymentIntentStatus(status))
);

export const shouldClearPaymentDraftAfterError = (error) => (
  shouldClearPaymentDraftForStatus(error?.paymentIntentStatus)
);

const wait = (delayMs) => new Promise((resolve) => setTimeout(resolve, delayMs));

export const pollPaymentIntentUntilReady = async (
  stripe,
  clientSecret,
  pollDelaysMs = POLL_DELAYS_MS
) => {
  let latestPaymentIntent = null;

  for (const delayMs of pollDelaysMs) {
    await wait(delayMs);
    const { error, paymentIntent } = await stripe.retrievePaymentIntent(clientSecret);

    if (error) {
      throw error;
    }

    if (paymentIntent) {
      latestPaymentIntent = paymentIntent;
    }

    if (!isProcessingPaymentStatus(paymentIntent?.status)) {
      return latestPaymentIntent;
    }
  }

  return latestPaymentIntent;
};

const requireAuthorizedOrPending = async (stripe, clientSecret, paymentIntent) => {
  if (isAuthorizedPaymentStatus(paymentIntent?.status)) {
    return paymentIntent;
  }

  if (isProcessingPaymentStatus(paymentIntent?.status)) {
    const resolvedIntent = await pollPaymentIntentUntilReady(stripe, clientSecret);

    if (isAuthorizedPaymentStatus(resolvedIntent?.status)) {
      return resolvedIntent;
    }

    if (isProcessingPaymentStatus(resolvedIntent?.status) || !resolvedIntent) {
      throw createPaymentProcessingError();
    }

    return resolvedIntent;
  }

  return paymentIntent;
};

export const resolveAuthorizedPayment = async ({
  stripe,
  clientSecret,
  confirmPayment,
}) => {
  if (!stripe || !clientSecret || typeof confirmPayment !== 'function') {
    throw new Error('Stripe payment confirmation is not ready');
  }

  const currentResult = await stripe.retrievePaymentIntent(clientSecret);
  if (currentResult?.error) {
    throw currentResult.error;
  }

  const currentIntent = currentResult?.paymentIntent;
  if (isAuthorizedPaymentStatus(currentIntent?.status)) {
    return {
      paymentIntent: currentIntent,
      skippedConfirm: true,
    };
  }

  if (isProcessingPaymentStatus(currentIntent?.status)) {
    const resolvedIntent = await requireAuthorizedOrPending(stripe, clientSecret, currentIntent);
    if (isAuthorizedPaymentStatus(resolvedIntent?.status)) {
      return {
        paymentIntent: resolvedIntent,
        skippedConfirm: true,
      };
    }
    throw createUnexpectedPaymentStatusError(resolvedIntent?.status);
  }

  const { error: confirmError, paymentIntent: confirmedIntent } = await confirmPayment();

  if (confirmError) {
    const retrieved = await stripe.retrievePaymentIntent(clientSecret);
    const recoveredIntent = !retrieved?.error ? retrieved?.paymentIntent : null;

    if (isAuthorizedPaymentStatus(recoveredIntent?.status)) {
      return {
        paymentIntent: recoveredIntent,
        skippedConfirm: false,
        recoveredAfterConfirmError: true,
      };
    }

    if (isProcessingPaymentStatus(recoveredIntent?.status)) {
      const resolvedIntent = await requireAuthorizedOrPending(stripe, clientSecret, recoveredIntent);
      if (isAuthorizedPaymentStatus(resolvedIntent?.status)) {
        return {
          paymentIntent: resolvedIntent,
          skippedConfirm: false,
          recoveredAfterConfirmError: true,
        };
      }
    }

    throw confirmError;
  }

  const resolvedIntent = await requireAuthorizedOrPending(stripe, clientSecret, confirmedIntent);
  if (isAuthorizedPaymentStatus(resolvedIntent?.status)) {
    return {
      paymentIntent: resolvedIntent,
      skippedConfirm: false,
    };
  }

  throw createUnexpectedPaymentStatusError(resolvedIntent?.status);
};

export const resolveExistingAuthorizedPayment = async ({
  stripe,
  clientSecret,
  initialPaymentIntent = null,
}) => {
  if (!stripe || !clientSecret) {
    throw new Error('Stripe payment confirmation is not ready');
  }

  let currentIntent = initialPaymentIntent;

  if (!currentIntent) {
    const currentResult = await stripe.retrievePaymentIntent(clientSecret);
    if (currentResult?.error) {
      throw currentResult.error;
    }
    currentIntent = currentResult?.paymentIntent;
  }

  const resolvedIntent = await requireAuthorizedOrPending(stripe, clientSecret, currentIntent);

  if (isAuthorizedPaymentStatus(resolvedIntent?.status)) {
    return {
      paymentIntent: resolvedIntent,
      skippedConfirm: true,
    };
  }

  throw createUnexpectedPaymentStatusError(resolvedIntent?.status);
};

const getDraftKey = (studioID) => {
  if (!studioID) return null;
  return `${PAYMENT_DRAFT_STORAGE_PREFIX}:${studioID}`;
};

const getCheckoutLockKey = (studioID) => {
  if (!studioID) return null;
  return `${CHECKOUT_LOCK_STORAGE_PREFIX}:${studioID}`;
};

const makeCheckoutLockOwnerID = () => (
  `${Date.now()}-${Math.random().toString(36).slice(2)}`
);

const getBrowserStorage = (storageName) => {
  if (typeof window === 'undefined') return null;

  try {
    return window[storageName] || null;
  } catch (error) {
    return null;
  }
};

const getDraftStorageCandidates = () => (
  [
    getBrowserStorage('sessionStorage'),
    getBrowserStorage('localStorage'),
  ].filter(Boolean)
);

const getDraftSessionStorage = () => getBrowserStorage('sessionStorage');
const getLegacyDraftLocalStorage = () => getBrowserStorage('localStorage');

const removeStorageItem = (storage, key) => {
  try {
    storage.removeItem(key);
  } catch (error) {
    // Ignore storage failures; storage is only a client-side recovery aid.
  }
};

const isValidReservationDraft = (draft, payloadSignature) => {
  const expiresAt = Number(draft?.reservation?.expiresAt);
  const nowEpochSeconds = Math.floor(Date.now() / 1000);

  return (
    draft?.payloadSignature === payloadSignature &&
    Boolean(draft?.reservation?.slotID) &&
    Boolean(draft?.reservation?.reservationToken) &&
    Boolean(draft?.reservation?.clientSecret) &&
    Boolean(draft?.reservation?.paymentIntentID) &&
    Number.isFinite(expiresAt) &&
    expiresAt > nowEpochSeconds
  );
};

export const getBookingCheckoutLockOwnerID = () => {
  const sessionStorage = getBrowserStorage('sessionStorage');
  const fallbackOwnerID = makeCheckoutLockOwnerID();

  if (!sessionStorage) {
    return fallbackOwnerID;
  }

  try {
    const existingOwnerID = sessionStorage.getItem(CHECKOUT_LOCK_OWNER_KEY);
    if (existingOwnerID) return existingOwnerID;

    sessionStorage.setItem(CHECKOUT_LOCK_OWNER_KEY, fallbackOwnerID);
    return fallbackOwnerID;
  } catch (error) {
    return fallbackOwnerID;
  }
};

export const acquireBookingCheckoutLock = ({
  studioID,
  payloadSignature,
  ownerID = getBookingCheckoutLockOwnerID(),
  ttlMs = CHECKOUT_LOCK_TTL_MS,
}) => {
  const key = getCheckoutLockKey(studioID);
  const localStorage = getBrowserStorage('localStorage');
  if (!key || !payloadSignature || !localStorage) {
    return { acquired: true, ownerID, storageAvailable: false };
  }

  const now = Date.now();
  const expiresAt = now + ttlMs;

  try {
    const rawExistingLock = localStorage.getItem(key);
    if (rawExistingLock) {
      const existingLock = JSON.parse(rawExistingLock);
      if (
        existingLock?.expiresAt > now &&
        existingLock?.ownerID &&
        existingLock.ownerID !== ownerID
      ) {
        return {
          acquired: false,
          ownerID,
          activeOwnerID: existingLock.ownerID,
          expiresAt: existingLock.expiresAt,
        };
      }
    }

    localStorage.setItem(
      key,
      JSON.stringify({
        ownerID,
        payloadSignature,
        expiresAt,
        updatedAt: now,
      })
    );

    const currentLock = JSON.parse(localStorage.getItem(key) || '{}');
    if (currentLock?.ownerID !== ownerID) {
      return {
        acquired: false,
        ownerID,
        activeOwnerID: currentLock?.ownerID || null,
        expiresAt: currentLock?.expiresAt || null,
      };
    }

    return { acquired: true, ownerID, expiresAt };
  } catch (error) {
    return { acquired: true, ownerID, storageAvailable: false };
  }
};

export const releaseBookingCheckoutLock = ({ studioID, ownerID }) => {
  const key = getCheckoutLockKey(studioID);
  const localStorage = getBrowserStorage('localStorage');
  if (!key || !ownerID || !localStorage) return;

  try {
    const currentLock = JSON.parse(localStorage.getItem(key) || '{}');
    if (currentLock?.ownerID === ownerID) {
      localStorage.removeItem(key);
    }
  } catch (error) {
    removeStorageItem(localStorage, key);
  }
};

export const refreshBookingCheckoutLock = ({
  studioID,
  ownerID,
  ttlMs = CHECKOUT_LOCK_TTL_MS,
}) => {
  const key = getCheckoutLockKey(studioID);
  const localStorage = getBrowserStorage('localStorage');
  if (!key || !ownerID || !localStorage) return false;

  try {
    const currentLock = JSON.parse(localStorage.getItem(key) || '{}');
    if (currentLock?.ownerID !== ownerID) {
      return false;
    }

    localStorage.setItem(
      key,
      JSON.stringify({
        ...currentLock,
        expiresAt: Date.now() + ttlMs,
        updatedAt: Date.now(),
      })
    );
    return true;
  } catch (error) {
    return false;
  }
};

export const startBookingCheckoutLockRefresh = ({
  studioID,
  ownerID,
  intervalMs = 30 * 1000,
}) => {
  if (
    typeof window === 'undefined' ||
    typeof window.setInterval !== 'function' ||
    typeof window.clearInterval !== 'function'
  ) {
    return () => {};
  }

  const timerID = window.setInterval(() => {
    refreshBookingCheckoutLock({ studioID, ownerID });
  }, intervalMs);

  return () => window.clearInterval(timerID);
};

export const loadBookingPaymentDraft = ({ studioID, payloadSignature }) => {
  const key = getDraftKey(studioID);
  if (!key || !payloadSignature) return null;

  const sessionStorage = getDraftSessionStorage();
  const legacyLocalStorage = getLegacyDraftLocalStorage();

  if (sessionStorage) {
    try {
      const raw = sessionStorage.getItem(key);
      if (raw) {
        const draft = JSON.parse(raw);

        if (!isValidReservationDraft(draft, payloadSignature)) {
          removeStorageItem(sessionStorage, key);
        } else {
          return draft.reservation;
        }
      }
    } catch (error) {
      removeStorageItem(sessionStorage, key);
    }
  }

  if (legacyLocalStorage) {
    try {
      const raw = legacyLocalStorage.getItem(key);
      if (!raw) return null;

      const draft = JSON.parse(raw);
      removeStorageItem(legacyLocalStorage, key);

      if (!isValidReservationDraft(draft, payloadSignature)) {
        return null;
      }

      if (sessionStorage) {
        try {
          sessionStorage.setItem(key, raw);
        } catch (error) {
          return null;
        }
      } else {
        return null;
      }

      return draft.reservation;
    } catch (error) {
      removeStorageItem(legacyLocalStorage, key);
    }
  }

  return null;
};

export const saveBookingPaymentDraft = ({ studioID, payloadSignature, reservation }) => {
  const key = getDraftKey(studioID);
  if (!key || !payloadSignature || !reservation) return;

  const serializedDraft = JSON.stringify({
    payloadSignature,
    reservation,
    savedAt: Date.now(),
  });

  const sessionStorage = getDraftSessionStorage();
  const legacyLocalStorage = getLegacyDraftLocalStorage();

  if (legacyLocalStorage) {
    removeStorageItem(legacyLocalStorage, key);
  }

  if (sessionStorage) {
    try {
      sessionStorage.setItem(key, serializedDraft);
    } catch (error) {
      // Ignore storage failures; in-memory reservation reuse still covers the current run.
    }
  }
};

export const clearBookingPaymentDraft = (studioID) => {
  const key = getDraftKey(studioID);
  if (!key) return;

  for (const storage of getDraftStorageCandidates()) {
    removeStorageItem(storage, key);
  }
};

const removeStorageItemsByPrefix = (storage, prefix) => {
  if (!storage || !prefix) return;

  try {
    for (let index = storage.length - 1; index >= 0; index -= 1) {
      const key = storage.key(index);
      if (key?.startsWith(prefix)) {
        storage.removeItem(key);
      }
    }
  } catch (error) {
    // Ignore storage failures; cleanup is best-effort.
  }
};

export const clearLegacyBookingPaymentDraftStorage = () => {
  removeStorageItemsByPrefix(
    getLegacyDraftLocalStorage(),
    `${PAYMENT_DRAFT_STORAGE_PREFIX}:`
  );
};

export const clearAllBookingPaymentStorage = () => {
  const storages = getDraftStorageCandidates();

  storages.forEach((storage) => {
    removeStorageItemsByPrefix(storage, `${PAYMENT_DRAFT_STORAGE_PREFIX}:`);
    removeStorageItemsByPrefix(storage, `${CHECKOUT_LOCK_STORAGE_PREFIX}:`);
    removeStorageItem(storage, CHECKOUT_LOCK_OWNER_KEY);
  });
};
